import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchBillingCompany, fetchBillingCompanyCities } from 'api/billingCompany';
import getDocTypeRegex from 'utils/getDocTypeRegex';
import { DOCUMENT_PATTERNS } from 'utils/safeRegexPatterns';
import {
  companyCreateOpenedTracker,
  companyFoundTracker,
  companyNotFoundTracker,
  companySearchPerformedTracker,
} from 'metrics/user-analytics/purchase';

export const BILLING_COMPANY_STATUS = {
  IDLE: 'idle',
  SEARCHING: 'searching',
  FOUND: 'found',
  NOT_FOUND: 'not_found',
  ERROR: 'error',
};

// Only used when the selected type has no safe validation rule. The hook must not invent length
// constraints in that case: field validation still blocks an invalid purchase, while the provider
// can resolve any non-empty numeric identifier accepted by tenant configuration.
const FALLBACK_SEARCHABLE_PATTERN = /^\d+$/;
const LOOKUP_DELAY = 500;

// `normalizeRegex` already defines which tenant regex sources are safe. Only anchored entries from
// that whitelist are validation rules; normalization-only entries fall back to a non-empty numeric
// shape instead of being mistaken for completion checks.
const SAFE_DOCUMENT_RULES = new Set(
  Object.keys(DOCUMENT_PATTERNS).filter((rule) => rule.startsWith('^') && rule.endsWith('$')),
);
const compiledDocumentRules = new Map();

/**
 * Reads the tenant rule for a document type. Configuration that is not reachable is not fatal:
 * eligibility falls back to a non-empty numeric shape rather than breaking the form.
 *
 * @param {string} documentType - Selected purchaser document brand value.
 * @returns {string} Configured regex source, or an empty string.
 */
const configuredDocumentRule = (documentType) => {
  try {
    return getDocTypeRegex('purchaser', documentType);
  } catch (error) {
    return '';
  }
};

/**
 * Compiles a configured rule once per source, accepting only the repository's safe whitelist.
 *
 * @param {string} rule - Configured regex source.
 * @returns {RegExp|null} Compiled matcher, or null when there is no usable rule.
 */
const compileDocumentRule = (rule) => {
  if (!SAFE_DOCUMENT_RULES.has(rule)) return null;
  if (compiledDocumentRules.has(rule)) return compiledDocumentRules.get(rule);

  let compiled = null;
  try {
    compiled = new RegExp(rule);
  } catch (error) {
    compiled = null;
  }
  compiledDocumentRules.set(rule, compiled);
  return compiled;
};

/**
 * Decides whether a document is complete enough to spend a provider request on, using the same
 * per-type rule the document field validates against.
 *
 * @param {string} normalizedDocument - Digits sent to the provider.
 * @param {string} rawDocument - Document as the field holds it, formatting included.
 * @param {string} documentType - Selected purchaser document brand value.
 * @returns {boolean} Whether the lookup may run for this document.
 */
const isSearchableDocument = (normalizedDocument, rawDocument, documentType) => {
  if (!normalizedDocument) return false;

  const rule = compileDocumentRule(configuredDocumentRule(documentType));
  if (!rule) return FALLBACK_SEARCHABLE_PATTERN.test(normalizedDocument);

  // A rule can be written against the digits the provider receives or against the formatted value
  // the field keeps; either reading means the buyer typed a complete identifier.
  return rule.test(normalizedDocument) || rule.test(rawDocument);
};

/**
 * Detects request cancellation across browser and client error shapes.
 *
 * @param {Error} error - Request error.
 * @returns {boolean} Whether the request was cancelled.
 */
const isAbortError = (error) =>
  error?.name === 'AbortError' || error?.code === 'ABORT_ERR' || error?.code === 20;

/**
 * Unwraps the first company result from supported API response shapes.
 *
 * @param {object|Array<object>} response - Potentially wrapped API payload.
 * @returns {object|null} The first result or null.
 */
const firstResult = (response) => {
  if (Array.isArray(response)) return response[0] || null;
  if (Array.isArray(response?.body)) return response.body[0] || null;
  return response?.body || response || null;
};

/**
 * Normalizes the un-serialized Sisorg billing-company payload.
 *
 * @param {object|Array<object>} response - Billing-company API response.
 * @returns {object|null} Normalized company or null when no match exists.
 */
export function normalizeBillingCompany(response) {
  const result = firstResult(response);
  const id = result?.id;
  const name = result?.name;

  if (!id || !name) return null;

  return {
    id: String(id),
    name,
    // Already translated to our own document vocabulary by saas-integrations; the funnel never
    // sees the provider's numeric id.
    documentType: result?.document_type || result?.documentType || '',
    address: result?.address || '',
  };
}

/**
 * Normalizes the city catalog into the option contract used by SelectRedux.
 *
 * @param {Array<object>} response - Billing-company city API response.
 * @returns {Array<object>} Select options.
 */
export function normalizeBillingCompanyCities(response) {
  if (!Array.isArray(response)) return [];

  return response
    .map((city) => {
      const value = city?.id;
      const label = city?.name;
      if (value === undefined || value === null || !label) return null;
      return { value: String(value), label };
    })
    .filter(Boolean);
}

/**
 * Debounced billing-company lookup and city-catalog loader.
 *
 * @param {object} params - Lookup inputs.
 * @param {string} params.purchaseToken - Current purchase token.
 * @param {string} params.document - Current purchaser document.
 * @param {string} params.documentType - Current purchaser document brand value (e.g. `NIT`).
 * @param {boolean} params.enabled - Whether the rollout is enabled for this form.
 * @param {string} [params.initialCompanyId] - Serialized/stored selected company id.
 * @param {string} [params.initialCompanyName] - Serialized/stored selected company name.
 * @param {string} [params.initialCompanyAddress] - Serialized/stored selected address/city.
 * @param {string} [params.initialCompanyDocument] - Document bound to a stored company.
 * @returns {object} Lookup state, company, city state, and clearSelection.
 */
const useBillingCompanyLookup = ({
  purchaseToken,
  document,
  documentType,
  enabled,
  initialCompanyId,
  initialCompanyName,
  initialCompanyAddress,
  initialCompanyDocument,
}) => {
  const rawDocument = useMemo(() => String(document || '').trim(), [document]);
  const normalizedDocument = useMemo(() => String(document || '').replace(/\D/g, ''), [document]);
  const normalizedDocumentType = useMemo(() => String(documentType || '').trim(), [documentType]);
  const documentIsSearchable = useMemo(
    () => isSearchableDocument(normalizedDocument, rawDocument, normalizedDocumentType),
    [normalizedDocument, normalizedDocumentType, rawDocument],
  );
  const normalizedInitialCompanyDocument = useMemo(
    () =>
      initialCompanyDocument === undefined
        ? null
        : String(initialCompanyDocument || '').replace(/\D/g, ''),
    [initialCompanyDocument],
  );
  const initialCompany = useMemo(() => {
    if (!initialCompanyId || !initialCompanyName) return null;
    if (
      normalizedInitialCompanyDocument !== null &&
      normalizedInitialCompanyDocument !== normalizedDocument
    )
      return null;
    return {
      id: String(initialCompanyId),
      name: initialCompanyName,
      address: initialCompanyAddress || '',
    };
  }, [
    initialCompanyAddress,
    initialCompanyId,
    initialCompanyName,
    normalizedDocument,
    normalizedInitialCompanyDocument,
  ]);

  const [status, setStatus] = useState(
    enabled && initialCompany ? BILLING_COMPANY_STATUS.FOUND : BILLING_COMPANY_STATUS.IDLE,
  );
  const [company, setCompany] = useState(enabled ? initialCompany : null);
  const [cities, setCities] = useState([]);

  const lookupGeneration = useRef(0);
  const lookupController = useRef(null);
  const citiesGeneration = useRef(0);
  const citiesController = useRef(null);
  const citiesToken = useRef(null);
  const hydrationToken = useRef(purchaseToken);
  const ignoredInitialCompanyId = useRef(null);

  const clearSelection = useCallback(() => {
    lookupGeneration.current += 1;
    lookupController.current?.abort();
    lookupController.current = null;
    ignoredInitialCompanyId.current = initialCompanyId ? String(initialCompanyId) : null;
    setCompany(null);
    setStatus(BILLING_COMPANY_STATUS.IDLE);
  }, [initialCompanyId]);

  useEffect(() => {
    if (hydrationToken.current !== purchaseToken) {
      hydrationToken.current = purchaseToken;
      ignoredInitialCompanyId.current = null;
    }

    lookupGeneration.current += 1;
    const generation = lookupGeneration.current;
    lookupController.current?.abort();
    lookupController.current = null;

    if (!enabled || !purchaseToken) {
      setCompany(null);
      setStatus(BILLING_COMPANY_STATUS.IDLE);
      return undefined;
    }

    if (
      initialCompany &&
      documentIsSearchable &&
      ignoredInitialCompanyId.current !== initialCompany.id
    ) {
      setCompany(initialCompany);
      setStatus(BILLING_COMPANY_STATUS.FOUND);
      return undefined;
    }

    setCompany(null);
    setStatus(BILLING_COMPANY_STATUS.IDLE);

    // The document type is a precondition, not an answer: the API rejects a search without
    // `document_type`, so an unresolved type keeps the lookup idle instead of spending a request
    // on a 422.
    if (!normalizedDocumentType) return undefined;

    // Length is the configured type's business, not this hook's: whatever the buyer's document
    // rule accepts is worth one request.
    if (!documentIsSearchable) return undefined;

    const timer = setTimeout(async () => {
      const controller = typeof AbortController === 'undefined' ? null : new AbortController();
      lookupController.current = controller;
      setStatus(BILLING_COMPANY_STATUS.SEARCHING);
      companySearchPerformedTracker();

      try {
        const response = await fetchBillingCompany(
          purchaseToken,
          normalizedDocument,
          normalizedDocumentType,
          controller?.signal,
        );
        if (generation !== lookupGeneration.current) return;

        const foundCompany = normalizeBillingCompany(response);
        if (foundCompany) {
          setCompany(foundCompany);
          setStatus(BILLING_COMPANY_STATUS.FOUND);
          companyFoundTracker();
        } else {
          setCompany(null);
          setStatus(BILLING_COMPANY_STATUS.NOT_FOUND);
          companyNotFoundTracker();
          companyCreateOpenedTracker();
        }
      } catch (error) {
        if (generation !== lookupGeneration.current || isAbortError(error)) return;
        setCompany(null);
        setStatus(BILLING_COMPANY_STATUS.ERROR);
        companyCreateOpenedTracker();
      } finally {
        if (generation === lookupGeneration.current) lookupController.current = null;
      }
    }, LOOKUP_DELAY);

    return () => {
      clearTimeout(timer);
      if (generation === lookupGeneration.current) {
        lookupController.current?.abort();
        lookupController.current = null;
      }
    };
  }, [
    documentIsSearchable,
    enabled,
    initialCompany,
    normalizedDocument,
    normalizedDocumentType,
    purchaseToken,
  ]);

  useEffect(() => {
    citiesGeneration.current += 1;
    citiesController.current?.abort();
    citiesController.current = null;
    citiesToken.current = null;
    setCities([]);
  }, [enabled, purchaseToken]);

  useEffect(() => {
    // The catalog also backs the address city, which is on screen from the start — so it cannot
    // wait for the lookup to come back empty, or the field would swap from a text input to a
    // select mid-capture.
    const shouldLoadCities = enabled && Boolean(purchaseToken);

    if (!shouldLoadCities || citiesToken.current === purchaseToken) return undefined;

    citiesToken.current = purchaseToken;
    citiesGeneration.current += 1;
    const generation = citiesGeneration.current;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    citiesController.current = controller;

    fetchBillingCompanyCities(purchaseToken, controller?.signal)
      .then((response) => {
        if (generation !== citiesGeneration.current) return;
        setCities(normalizeBillingCompanyCities(response));
      })
      .catch((error) => {
        if (generation !== citiesGeneration.current || isAbortError(error)) return;
        // A catalog that fails to load is not reported: the buyer sees the plain text city
        // input and the purchase carries on without a company city.
        setCities([]);
      })
      .finally(() => {
        if (generation === citiesGeneration.current) citiesController.current = null;
      });

    return undefined;
  }, [enabled, purchaseToken]);

  useEffect(
    () => () => {
      lookupController.current?.abort();
      citiesController.current?.abort();
    },
    [],
  );

  return {
    status,
    company,
    cities,
    normalizedDocument,
    clearSelection,
  };
};

export default useBillingCompanyLookup;
